// @flow
import React, { useCallback, useContext, useMemo } from 'react';
import { featureIsEnabled } from '@ta/common.features';
import { RouteContext } from '@ta/common.routing';
import { useVDOMPortal } from '@ta/lithium.portals';
import {
  ExplicitPreferencesUI,
  useExplicitPreferencesState,
  type ExplicitPreferencesMode,
  type ExplicitPreferencesState,
} from '@ta/onboarding.explicit-preferences';

type ExplicitPreferencesResult = {|
  /**
   * Start the flow if it's enabled
   */
  open: () => mixed,

  /**
   * True if the Explicit Preferences flow is available. In addition to a feature check, this
   * depends on the mode passed in and the current page.
   */
  isEnabled: boolean,

  /**
   * The current state of the Explicit Preferences flow, if enabled. This can be used to display
   * a CTA containing the relevant personalization data.
   */
  state: ExplicitPreferencesState,
|};

/**
 * A hook to start up the Explicit Preferences flow. This decides if Explicit Preferences should
 * be enabled, and connects the state machine (useExplicitPreferencesState) to the UI
 * (ExplicitPreferencesUI).
 */
const useExplicitPreferencesModal = (
  mode: ExplicitPreferencesMode,
  onRefresh?: () => Promise<*>,
): ExplicitPreferencesResult => {
  const {
    route: { page, geo },
  } = useContext(RouteContext);

  const isEnabled = useMemo(() => {
    switch (mode) {
      case 'ONBOARDING':
        return featureIsEnabled('cx_explicit_preferences_onboarding');

      case 'ONETAP':
        return featureIsEnabled('cx_explicit_preferences_onetap');

      // The edit flow is only shown on scoped /Tourism
      case 'EDITING':
        return (
          (featureIsEnabled('cx_explicit_preferences_scoped_home_cta') ||
            featureIsEnabled('cx_explicit_preferences_scoped_home_cta_always_edit')) &&
          page === 'Home' &&
          geo != null
        );
      case 'GEOPILL':
        return featureIsEnabled('cx_explicit_preferences_geopill');

      default:
        throw new Error(`useExplicitPreferencesModal: invalid mode: ${(mode: empty)}`);
    }
  }, [mode, page, geo]);

  const state = useExplicitPreferencesState({
    mode,
    scopedGeoId: +geo || null,
    pause: !isEnabled,
  });

  const { open } = useVDOMPortal(({ close }) => (
    <ExplicitPreferencesUI mode={mode} state={state} onClose={close} onRefresh={onRefresh} />
  ));

  const openModal = useCallback(() => {
    if (isEnabled) {
      open();
    }
  }, [isEnabled, open]);

  return {
    open: openModal,
    isEnabled,
    state,
  };
};

export default useExplicitPreferencesModal;
